import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core'
import { useImageStore } from '../../store/ImageStore'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import clsx from 'clsx'
import XMark from '../ui/icons/XMark'
import RasterImage from '../ui/image'
import { SortableItem } from '../ui/sortable-item'
import { useState } from 'react'
import { Provider } from '../provider'
import '../../instrument'
import { useShallow } from 'zustand/shallow'

function RasterPreview({ showBorder }: { showBorder: boolean }) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const [selected, setPhoto, reorderPhotos] = useImageStore(
    useShallow((state) => [state.selected, state.setPhoto, state.reorderPhotos]),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    setIsDragging(false)
    reorderPhotos(event)
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
    setIsDragging(true)
  }

  if (!selected?.length) {
    return (
      <>
        <h3 className="text-xl font-medium pb-3">Selected assets ({selected?.length}):</h3>

        <div className={clsx('relative h-36 w-full mb-2', { 'border rounded-md': showBorder })}>
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            Please make a selection below.
          </span>
        </div>
      </>
    )
  }

  return (
    <>
      <h3 className="text-xl font-medium pb-3">Selected assets ({selected?.length}):</h3>
      <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={selected} strategy={verticalListSortingStrategy}>
          <div className="flex gap-x-2 flex-wrap">
            {selected?.map((image: Image) => (
              <div key={image.id} className="group image relative inline-block animate-fade-in">
                <button
                  type="button"
                  onClick={() => {
                    setPhoto({ photo: image })
                  }}
                  aria-label="Remove image"
                  className={clsx(
                    'z-50 text-white border-[3px] border-white absolute -top-2.5 -right-2.5 h-8 w-8 flex justify-center items-center bg-primary rounded-full p-1 hover:bg-primary-dark transition-colors bg-red-500',
                    { 'opacity-0': isDragging },
                  )}
                >
                  <XMark />
                </button>

                <SortableItem id={image.id} isDragging={isDragging}>
                  <RasterImage image={image} displayName={false} thumbnail chooseImage={() => {}} />
                </SortableItem>
              </div>
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <SortableItem id={activeId} isDragging={isDragging}>
              <RasterImage
                image={selected.find((image: Image) => image.id === activeId)!}
                displayName={false}
                thumbnail
              />
            </SortableItem>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  )
}

export function Preview({
  config,
  initialValue,
  showBorder = true,
}: {
  config: Settings
  initialValue: unknown
  showBorder?: boolean
}) {
  return (
    <Provider config={config} initialValue={initialValue}>
      <RasterPreview showBorder={showBorder} />
    </Provider>
  )
}
